import { signal } from "omi";
import groupBy from "lodash-es/groupBy";
import orderBy from "lodash-es/orderBy";

import { networkSignal } from "@/utils/signals";
import { Course, Project } from "@/api";
import { initApi } from "@/utils";

import { getOrder } from "../../utils";

export type UseProjectStore = ReturnType<typeof useProjects>;

const projects = networkSignal<Project[]>({ default: [] });
const projectsByCategory = signal<[string, Project[]][]>([]);
const categories = signal<string[]>([]);

export function useProjects() {
  const fetch = async () => {
    const res = await projects.run(getProjects);
    setProjectsByCategory(res);
  };

  return {
    projects: projects.signal,
    projectsByCategory,
    fetch,
    categories,
    updateProjectStatus,
    getProjectsForCourse,
    getProjectById: getProjectByIdSelector(projects.signal.data.value || []),
  };
}

async function getProjects(): Promise<Project[]> {
  const api = initApi();
  return await api.getProjects();
}

function getProjectByIdSelector(projects: Project[]): (id: Project["id"]) => Project | undefined {
  return id => projects.find(p => p.id === id);
}

function orderAndGroup(projects: Project[]): [string, Project[]][] {
  const groupByCategory = groupBy(projects, p => p.metadata?.category || "Misc");
  const orderedByCategory = orderBy(Object.entries(groupByCategory), ([category]) => category);
  return orderedByCategory.map(([category, projects]) => [
    category,
    orderBy(projects, (p, idx) => [getOrder(p)?.seriesOrder || idx, getOrder(p)?.projectOrder || idx]),
  ]);
}

function getProjectsForCourse(course: Course): Project[] {
  if (projects.signal.data.value == null) {
    return [];
  }

  const courseProjects = projects.signal.data.value.filter(p => course.metadata?.projects?.includes(p.id!));
  return orderBy(courseProjects, (p, idx) => [getOrder(p)?.seriesOrder || idx, getOrder(p)?.projectOrder || idx]);
}

function updateProjectStatus(projectId: string, status: Project["status"]) {
  if (projects.signal.data.value == null) {
    return;
  }

  const allProjects = [...projects.signal.data.value];
  const project = allProjects.find(p => p.id === projectId);
  if (project) {
    project.status = status;
    projects.signal.data.value = allProjects;
    setProjectsByCategory(allProjects);
  }
}

function setProjectsByCategory(projects: Project[] | undefined) {
  projectsByCategory.value = orderAndGroup(projects || []);
  categories.value = projectsByCategory.value.map(([category]) => category);
}
